// Surface 1 · Library (/frameworks).
//
// Spec: specs/consumer-frameworks-strings.md § Surface 1 · Library.
//
// Two-band grouping:
//   - RECOMMENDED NEXT: items whose server-returned recommendation_band is
//     'recommended_next'. Shows a note under the band header.
//   - ALL FRAMEWORKS: everything else.
//
// For the Oct-30 slice, recommendation_band is always null (tenant.current_
// level[domain] not yet wired on the backend). The code paths are built so
// that lighting up that signal later is a one-line backend change — this
// page already does the right thing when real bands come back.
//
// Domain filter chips: All + the 8 domains. Clicking a chip narrows the
// list; clicking "All" clears. The chips use the chip pattern from the
// mock (filled when active, bordered otherwise).
//
// Search is V1.x — it ships as a disabled stub so the mock chrome reads
// right without faking an implementation.

import { useEffect, useMemo, useState } from 'react';
import { ALL_DOMAINS, type Domain, type MeFrameworkLibraryItem } from '@gc-coach/shared';
import { getMeFrameworks } from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import { FrameworkCard } from './FrameworkCard.js';
import styles from './FrameworksLibraryPage.module.css';

type DomainFilter = Domain | 'all';

export function FrameworksLibraryPage() {
  const { t } = useI18n();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ok'; items: MeFrameworkLibraryItem[] }
  >({ kind: 'loading' });
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getMeFrameworks();
      if (cancelled) return;
      if (!result.ok) {
        setState({ kind: 'error', message: t('frameworks.library.errorGeneric') });
        return;
      }
      setState({ kind: 'ok', items: [...result.data.items] });
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const filtered = useMemo(() => {
    if (state.kind !== 'ok') return [];
    if (domainFilter === 'all') return state.items;
    return state.items.filter((i) => i.domain === domainFilter);
  }, [state, domainFilter]);

  const recommended = filtered.filter((i) => i.recommendation_band === 'recommended_next');
  const other = filtered.filter((i) => i.recommendation_band !== 'recommended_next');

  return (
    <div className={styles.pad}>
      <div className={styles.wrap}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>{t('frameworks.library.breadcrumb')}</span>
          <h1 className={styles.title}>{t('frameworks.library.title')}</h1>
          <p className={styles.lead}>{t('frameworks.library.lead')}</p>
        </header>

        <div className={styles.chipsRow}>
          <button
            type="button"
            className={`${styles.chip} ${domainFilter === 'all' ? styles.chipOn : ''}`}
            onClick={() => setDomainFilter('all')}
          >
            {t('frameworks.library.filterAll')}
          </button>
          {ALL_DOMAINS.map((d) => (
            <button
              key={d}
              type="button"
              className={`${styles.chip} ${domainFilter === d ? styles.chipOn : ''}`}
              onClick={() => setDomainFilter(d)}
            >
              {t(`frameworks.domain.${d}`)}
            </button>
          ))}
          <span className={styles.spacer} />
          <span className={styles.searchStub} aria-hidden>
            {t('frameworks.library.searchPlaceholder')}
          </span>
        </div>

        {state.kind === 'loading' && (
          <div className={styles.grid}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        )}

        {state.kind === 'error' && (
          <div className={styles.messagePanel}>
            <p>{state.message}</p>
          </div>
        )}

        {state.kind === 'ok' && filtered.length === 0 && (
          <div className={styles.messagePanel}>
            <h3 className={styles.messageTitle}>
              {domainFilter === 'all'
                ? t('frameworks.library.emptyNoneAtAllTitle')
                : t('frameworks.library.emptyInDomainTitle')}
            </h3>
            <p>
              {domainFilter === 'all'
                ? t('frameworks.library.emptyNoneAtAllBody')
                : t('frameworks.library.emptyInDomainBody')}
            </p>
            {domainFilter !== 'all' && (
              <button
                type="button"
                className={styles.messageButton}
                onClick={() => setDomainFilter('all')}
              >
                {t('frameworks.library.emptyClearFilter')}
              </button>
            )}
          </div>
        )}

        {state.kind === 'ok' && filtered.length > 0 && (
          <>
            {recommended.length > 0 && (
              <>
                <div className={styles.bandHeader}>
                  <span className={styles.bandEyebrow}>
                    {t('frameworks.library.bandRecommended')}
                  </span>
                  <span className={styles.bandNote}>
                    {t('frameworks.library.bandRecommendedNote')}
                  </span>
                </div>
                <div className={`${styles.grid} ${styles.gridWithBottomSpace}`}>
                  {recommended.map((i) => (
                    <FrameworkCard key={i.id} item={i} tenantCurrentLevel={null} />
                  ))}
                </div>
              </>
            )}

            <div className={styles.bandHeader}>
              <span className={`${styles.bandEyebrow} ${styles.bandEyebrowMuted}`}>
                {t('frameworks.library.bandAll')}
              </span>
            </div>
            <div className={styles.grid}>
              {other.map((i) => (
                <FrameworkCard key={i.id} item={i} tenantCurrentLevel={null} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
